import React, { FC, useState } from "react";
import { useDispatch } from "react-redux";

import { MISSION_DEPLOYMENT_ACTIONS } from "Utils/constants";
import { FMSLogger } from "FMSLogger";
import { EMPTY_ICON } from "Utils";
import { setActiveTooltipVehicleId } from "Slices/appState";

import LocationOpenedIcon from "../../assets/location-opened.svg";
import LocationOpenedIconBlack from "../../assets/location-black-arrow.svg";
import {
  VehicleItemStyled,
  FriendlyNameStyled,
  DetailsStyled,
  VehicleItemLabelStyled,
  VehicleItemTextStyled,
  VehicleDriveModeStyled,
  VehicleItemLineStyled,
  ImgStyled,
  DetailsBlockStyled,
  TopContentStyled,
  TopLeftContentStyled,
  DetailsIconStyled,
} from "./styles";

const missionDeploymentActionsLogger = FMSLogger.byPrefix(
  MISSION_DEPLOYMENT_ACTIONS
);

type DriveModText = {
  [key: string]: string;
};

const DRIVER_MODE_TEXT: DriveModText = {
  AUTO: "AUTONOMOUS",
  AUTONOMOUS: "AUTONOMOUS",
  MANUAL: "MANUAL DRIVER",
  REMOTE_CONTROL: "REMOTE CONTROL",
  STOPPING: "STOPPING",
  IDLE: "IDLE",
  OFFLINE: "OFFLINE",
};

type VehicleItemProps = {
  vehicleData: Vehicle;
  setMissionPlannerData: (missionPlannerData: unknown) => void;
  vehicleType: VehicleType | undefined;
  currentSemanticMap: SemanticMap | null;
  vehicleUpdatedData: VehiclesStateDataItem | null;
  hoveredVehicleId: string | null;
  setHoveredVehicleId: (vehicleId: string | null) => void;
  focusedVehicleIds: Set<string>;
};

export const VehicleItem: FC<VehicleItemProps> = ({
  vehicleData,
  vehicleType,
  vehicleUpdatedData,
  hoveredVehicleId,
  setHoveredVehicleId,
  focusedVehicleIds,
}) => {
  const dispatch = useDispatch();
  const [showDetails, setShowDetails] = useState(false);
  if (!vehicleType) {
    return <></>;
  }

  const { driveMode, webstate, vbStatus, state, id, name } = vehicleData;

  const {
    t_drive_mode: tDriveMode,
    t_vehicle_status: tVehicleStatus,
    t_virtual_bumper_status: tVirtualBumperStatus,
    t_web_status: tWebStatus,
    online_status,
  }: VehicleState | { [key: string]: string | null } = vehicleUpdatedData?.[
    id
  ] ?? {};

  const currentDriveMod =
    online_status === "OFFLINE"
      ? DRIVER_MODE_TEXT["OFFLINE"]
      : DRIVER_MODE_TEXT[tDriveMode || driveMode] || "";
  const currentVehicleStatus = tVehicleStatus || webstate || "UNAVAILABLE";
  const currentVirtualBumperStatus =
    tVirtualBumperStatus || vbStatus || "UNAVAILABLE";
  const currentWebStatus = tWebStatus || state || "UNAVAILABLE";

  const openShowDetails = () => {
    missionDeploymentActionsLogger.debug(`toggle vehicle details for ${id}`);
    setShowDetails(!showDetails);
  };

  const handleMouseEnter = () => {
    setHoveredVehicleId(vehicleData.id);
  };

  const handleMouseLeave = () => {
    setHoveredVehicleId(null);
  };

  const handleClick = () => {
    // Dispatch action to show tooltip on map
    openShowDetails();
    dispatch(setActiveTooltipVehicleId(vehicleData.id));
  };

  const isFocused = focusedVehicleIds.has(vehicleData.id);
  const isHovered = hoveredVehicleId === vehicleData.id;

  return (
    <VehicleItemStyled
      showDetails={showDetails}
      isFocused={isFocused}
      isHovered={isHovered}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <TopContentStyled>
        <ImgStyled src={vehicleType.image?.link || EMPTY_ICON} />
        <TopLeftContentStyled>
          <VehicleItemLineStyled>
            <FriendlyNameStyled>{name}</FriendlyNameStyled>
            {currentDriveMod && (
              <VehicleDriveModeStyled text={currentDriveMod.toUpperCase()}>
                {currentDriveMod}
              </VehicleDriveModeStyled>
            )}
          </VehicleItemLineStyled>
          <DetailsStyled
            data-test-id={`vehicle_${name}_details`}
            showDetails={showDetails}
          >
            Details
            <DetailsIconStyled
              showDetails={showDetails}
              src={showDetails ? LocationOpenedIconBlack : LocationOpenedIcon}
              alt="Location"
            />
          </DetailsStyled>
        </TopLeftContentStyled>
      </TopContentStyled>
      {showDetails && (
        <DetailsBlockStyled>
          <VehicleItemLabelStyled>Vehicle Type</VehicleItemLabelStyled>
          <VehicleItemTextStyled>
            {vehicleType.name.toLowerCase()}
          </VehicleItemTextStyled>
          <VehicleItemLabelStyled>Vehicle Status</VehicleItemLabelStyled>
          <VehicleItemTextStyled>{currentVehicleStatus}</VehicleItemTextStyled>
          <VehicleItemLabelStyled>Virtual Bumper</VehicleItemLabelStyled>
          <VehicleDriveModeStyled
            marginBottom={true}
            text={currentVirtualBumperStatus.toUpperCase()}
          >
            {currentVirtualBumperStatus}
          </VehicleDriveModeStyled>
          <VehicleItemLabelStyled>
            DriveMod Software Status
          </VehicleItemLabelStyled>
          <VehicleDriveModeStyled
            marginBottom={true}
            text={currentWebStatus.toUpperCase()}
          >
            {currentWebStatus === "CALL_FOR_TELEOP"
              ? "Vehicle Stuck"
              : currentWebStatus}
          </VehicleDriveModeStyled>
        </DetailsBlockStyled>
      )}
    </VehicleItemStyled>
  );
};
